import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Box, Chip, Alert
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

const EXEMPTION_TYPES = [
  { value: 'resale', label: 'Resale' },
  { value: 'nonprofit', label: 'Nonprofit' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  customer_id: '', exemption_type: 'nonprofit', certificate_number: '',
  issuing_state: '', valid_from: '', valid_until: '', notes: ''
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(status) {
  const map = { active: 'success', expired: 'default', revoked: 'error' };
  return map[status] || 'default';
}

export default function TaxExemptionManagement() {
  const [exemptions, setExemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const data = await billingApi.get('/api/admin/tax/exemptions');
      setExemptions(data.exemptions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      setError('');
      if (!form.customer_id || !form.exemption_type || !form.valid_from) {
        setError('Customer ID, exemption type, and valid from date are required');
        return;
      }
      await billingApi.post('/api/admin/tax/exemptions', {
        ...form,
        issuing_state: form.issuing_state || null,
        valid_until: form.valid_until || null,
        notes: form.notes || null
      });
      setDialog(false);
      setForm({ ...EMPTY_FORM });
      setSuccess('Tax exemption created');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleRevoke(id) {
    if (!window.confirm('Revoke this tax exemption? The customer will be charged tax on future purchases.')) return;
    try {
      await billingApi.post(`/api/admin/tax/exemptions/${id}/revoke`);
      setSuccess('Exemption revoked');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  const columns = useMemo(() => [
    {
      field: 'customer_email',
      headerName: 'Customer',
      width: 200,
      renderCell: (row) => (
        <Box>
          <Typography variant="body2">{row.customer_email || '—'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace", fontSize: '0.65rem' }}>
            {row.customer_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'exemption_type',
      headerName: 'Type',
      width: 120,
      type: 'singleSelect',
      filterOptions: ['resale', 'nonprofit', 'government', 'other'],
      renderCell: (row) => <Chip label={row.exemption_type} size="small" variant="outlined" />,
    },
    {
      field: 'certificate_number',
      headerName: 'Certificate #',
      width: 140,
      renderCell: (row) => row.certificate_number || '—',
    },
    {
      field: 'issuing_state',
      headerName: 'State',
      width: 100,
      type: 'singleSelect',
      filterOptions: ['', 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'],
      renderCell: (row) => row.issuing_state || 'All',
    },
    {
      field: 'valid_from',
      headerName: 'Valid From',
      width: 140,
      type: 'date',
      renderCell: (row) => formatDate(row.valid_from),
    },
    {
      field: 'valid_until',
      headerName: 'Valid Until',
      width: 140,
      type: 'date',
      renderCell: (row) => row.valid_until ? formatDate(row.valid_until) : 'Indefinite',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      type: 'singleSelect',
      filterOptions: ['active', 'expired', 'revoked'],
      renderCell: (row) => (
        <Chip label={row.status} color={statusColor(row.status)} size="small" variant="outlined" />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (row) => row.status === 'active' ? (
        <Button size="small" color="error" onClick={() => handleRevoke(row.id)}>Revoke</Button>
      ) : null,
    },
  ], []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <AdminPageSkeleton bare rows={5} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tax Exemptions</Typography>
        <Button variant="contained" onClick={() => setDialog(true)}
          sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
          Add Exemption
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper variant="outlined" sx={{ height: 600 }}>
        <AdminTable
          rows={exemptions}
          columns={columns}
          defaultSort={[{ field: 'valid_from', direction: 'desc' }]}
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tax Exemption</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Customer</Typography>
          <TextField
            fullWidth label="Customer ID" value={form.customer_id}
            onChange={e => setForm({ ...form, customer_id: e.target.value })}
            helperText="UUID of the customer record"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Exemption Details</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Type</InputLabel>
              <Select value={form.exemption_type} label="Type"
                onChange={e => setForm({ ...form, exemption_type: e.target.value })}>
                {EXEMPTION_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Certificate #" value={form.certificate_number}
              onChange={e => setForm({ ...form, certificate_number: e.target.value })}
              sx={{ flex: 1 }}
            />
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Issuing State (optional)</InputLabel>
            <Select value={form.issuing_state} label="Issuing State (optional)"
              onChange={e => setForm({ ...form, issuing_state: e.target.value })}>
              <MenuItem value="">All States (blanket exemption)</MenuItem>
              {US_STATES.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Validity Period</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth type="date" label="Valid From" value={form.valid_from}
              onChange={e => setForm({ ...form, valid_from: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth type="date" label="Valid Until (optional)" value={form.valid_until}
              onChange={e => setForm({ ...form, valid_until: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for indefinite"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Notes</Typography>
          <TextField
            fullWidth multiline rows={2} label="Notes (optional)" value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
            Create Exemption
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
